/**
 * The record page's one request — §7.1.
 *
 * Everything the Overview needs arrives together: the three pills, a count
 * for every tab, what is next up, and the peeks. Eight separate fetches would
 * be eight round trips before anybody sees anything, on a phone, on a truck.
 *
 * `reload` is exported because the page writes: adding a note changes the
 * Activity count, completing a task changes Next up, and a screen that says
 * "31" next to a tab holding 32 things is a screen nobody trusts again.
 */
import { useCallback, useEffect, useState } from 'react'
import type { ActivityKind, ActivityVisibility, AnyRecordType } from '@rowted/shared'
import { request } from './api.js'
import { REQUEST_CREATED_EVENT } from '../requests/open-request.js'

export interface RecordPill {
  value: string
  label: string
  tone?: 'danger'
  /**
   * Money only some roles see — drawn on `surface-internal` with the purple
   * lock (the visit page's *$8,888 projected cost*, folder 50 Board 1).
   * Opt-in: a pill without it draws exactly as before.
   */
  internal?: boolean
}

export interface RecordActivity {
  id: string
  /** `appointment` too, at runtime — a stored kind the composer never offers; see `appointment`. */
  kind: ActivityKind
  title: string
  body: string
  dueAt: string | null
  doneAt: string | null
  assigneeId: string | null
  assigneeName: string | null
  visibility: ActivityVisibility
  createdAt: string
  authorName: string | null
  pinnedAt: string | null
  durationMinutes?: number | null
  /** A site visit's facts (`new-request.md` §15.1); present only on an appointment. */
  appointment?: AppointmentFacts
  /**
   * One occurrence of a repeating meeting — its own day in the series — when
   * the row is that occurrence: Next up shows a repeating meeting as its next
   * open one, and its tick is that one's alone (0152).
   */
  occurrence?: string | null
}

/** What a tick names: the activity, or `id@day` for one occurrence of a repeating meeting. */
export function completeKey(item: Pick<RecordActivity, 'id' | 'occurrence'>): string {
  return item.occurrence ? `${item.id}@${item.occurrence}` : item.id
}

/**
 * Ticks what a Next up or Upcoming row names done (its `completeKey`): one
 * occurrence of a repeating meeting by itself (`POST
 * /activity/occurrences/:id/done`, 0152 — ticking the meeting would draw
 * every occurrence done), anything else as it always was.
 */
export function completeActivity(key: string): Promise<unknown> {
  const [id, occurrence] = key.split('@')
  return occurrence
    ? request(`/activity/occurrences/${id}/done`, { method: 'POST', body: { occurrence, done: true } })
    : request(`/activities/${id}/done`, { method: 'POST', body: { done: true } })
}

/** What the server says about a site visit — `server/src/services/activities.ts`. */
export interface AppointmentFacts {
  jobId: string | null
  jobTitle: string | null
  jobReference: string | null
  fromRequest: boolean
  propertyId: string | null
  propertyName: string | null
  propertyLine: string | null
  propertyCity: string | null
  propertyState: string | null
  contactFirstName: string | null
  confirmedAt: string | null
  confirmedVia: 'sms' | 'email' | 'phone' | null
  sent: 'sms' | 'email' | null
  wanted: string | null
  leadSource: string | null
  budget: string | null
}

export interface JobPeek {
  id: string
  title: string
  reference: string
  propertyName: string | null
  status: string
  nextAction: string | null
  nextActionUrgent: boolean
  expected: string
  /* What the row's mark is drawn from — the four `jobMark` reads. */
  serviceLine: string | null
  divisionIcon: string | null
  divisionColour: string | null
  jobType: string | null
}

export interface VisitPeek {
  id: string
  scheduledDate: string
  description: string
  crewName: string | null
  status: string
  jobTitle: string | null
}

export interface RecordSummary {
  pills: RecordPill[]
  counts: Record<string, number>
  nextUp: RecordActivity[]
  peeks: { jobs: JobPeek[]; visits: VisitPeek[]; notes: RecordActivity[] }
  /**
   * Whether this person has hidden the Set up card here (`quiet-records.md`
   * §5). On the summary rather than its own request, so the card does not
   * arrive a beat after the rest of Overview.
   *
   * Optional on the client alone: a page cached from before migration 0111
   * has no such field, and `summary.quiet?.hidden` reading undefined means
   * "not hidden", which is the right answer for a payload that predates the
   * question.
   */
  quiet?: { hidden: boolean }
  /**
   * A job New request made with no site visit (`new-request.md` §15.1): Next
   * up says *No site visit yet* with **Book a site visit**. Absent on records
   * that are not jobs and in a Rowted Admin summary.
   */
  requestWithoutVisit?: boolean
}

/**
 * The pills and counts across a record's tabs.
 *
 * `options.request` is how a Rowted Admin record asks its own side: the path
 * is the same shape, so an admin passes `adminRequest` and the summary comes
 * from `/api/v1/platform/records/client/:id/summary`. Without it the shell
 * would have been forked per side, which is the thing the record contract
 * exists to prevent.
 *
 * A platform summary fills `pills` and `counts` and leaves `nextUp` and
 * `peeks` empty: a client has no activities to be next up.
 */
export function useRecordSummary(
  object: AnyRecordType,
  id: string | undefined,
  options: { request?: typeof request } = {},
) {
  const [summary, setSummary] = useState<RecordSummary | null>(null)
  const [error, setError] = useState<string | null>(null)

  const send = options.request ?? request

  const load = useCallback(async () => {
    if (!id) return
    try {
      setSummary(await send<RecordSummary>(`/records/${object}/${id}/summary`))
      setError(null)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That could not be loaded.')
    }
  }, [object, id, send])

  useEffect(() => { void load() }, [load])

  /*
   * A New request created or booked while this record is on screen — *Book a
   * time* from its own Next up — changes Next up, so the summary is asked again.
   */
  useEffect(() => {
    const again = () => { void load() }
    window.addEventListener(REQUEST_CREATED_EVENT, again)
    return () => window.removeEventListener(REQUEST_CREATED_EVENT, again)
  }, [load])

  return { summary, error, reload: load }
}
